import { IJsonQueryRangeValue } from '@cian/api-models/common/json_query';
import * as React from 'react';

import { useDebouncedCallback } from '../../../utils/useDebouncedCallback';
import { RangeInput } from '../common/RangeInput';
import { Filter } from '../common/Filter';
import { InlineFilterGroup } from '../common/InlineFilterGroup';
import { InlineFilter } from '../common/InlineFilter';
import { FOfferType } from '../../../../../JsonQuery';

interface ISquareMeterAreaProps {
  offerType: FOfferType;
  totalArea: IJsonQueryRangeValue;
  roomArea: IJsonQueryRangeValue;
  kitchenArea: IJsonQueryRangeValue;
  livingArea: IJsonQueryRangeValue;
  onTotalAreaMinChange(value: number | null): void;
  onTotalAreaMaxChange(value: number | null): void;
  onRoomAreaMinChange(value: number | null): void;
  onRoomAreaMaxChange(value: number | null): void;
  onKitchenAreaMinChange(value: number | null): void;
  onKitchenAreaMaxChange(value: number | null): void;
  onLivingAreaMinChange(value: number | null): void;
  onLivingAreaMaxChange(value: number | null): void;
}

export const SquareMeterArea: React.FC<ISquareMeterAreaProps> = ({
  offerType,
  totalArea: { gte: totalAreaMin = null, lte: totalAreaMax = null },
  roomArea: { gte: roomAreaMin = null, lte: roomAreaMax = null },
  kitchenArea: { gte: kitchenAreaMin = null, lte: kitchenAreaMax = null },
  livingArea: { gte: livingAreaMin = null, lte: livingAreaMax = null },
  onTotalAreaMinChange,
  onTotalAreaMaxChange,
  onRoomAreaMinChange,
  onRoomAreaMaxChange,
  onKitchenAreaMinChange,
  onKitchenAreaMaxChange,
  onLivingAreaMinChange,
  onLivingAreaMaxChange,
}) => {
  const isRoomOfferType = offerType === FOfferType.Room;

  const handleTotalAreaMinChange = useDebouncedCallback((gte: number | null) => {
    onTotalAreaMinChange(gte);
  }, 300);

  const handleTotalAreaMaxChange = useDebouncedCallback((gte: number | null) => {
    onTotalAreaMaxChange(gte);
  }, 300);

  const handleRoomAreaMinChange = useDebouncedCallback((gte: number | null) => {
    onRoomAreaMinChange(gte);
  }, 300);

  const handleRoomAreaMaxChange = useDebouncedCallback((gte: number | null) => {
    onRoomAreaMaxChange(gte);
  }, 300);

  const handleKitchenAreaMinChange = useDebouncedCallback((gte: number | null) => {
    onKitchenAreaMinChange(gte);
  }, 300);

  const handleKitchenAreaMaxChange = useDebouncedCallback((gte: number | null) => {
    onKitchenAreaMaxChange(gte);
  }, 300);

  const handleLivingAreaMinChange = useDebouncedCallback((gte: number | null) => {
    onLivingAreaMinChange(gte);
  }, 300);

  const handleLivingAreaMaxChange = useDebouncedCallback((gte: number | null) => {
    onLivingAreaMaxChange(gte);
  }, 300);

  return (
    <Filter
      label={
        <>
          Площадь, м<sup>2</sup>
        </>
      }
    >
      <InlineFilterGroup>
        <InlineFilter prefix="Общая">
          <RangeInput
            min={1}
            max={9999}
            valueMin={totalAreaMin}
            valueMax={totalAreaMax}
            onChangeMin={handleTotalAreaMinChange}
            onChangeMax={handleTotalAreaMaxChange}
          />
        </InlineFilter>
        {isRoomOfferType && (
          <InlineFilter prefix="Комната">
            <RangeInput
              min={1}
              max={9999}
              valueMin={roomAreaMin}
              valueMax={roomAreaMax}
              onChangeMin={handleRoomAreaMinChange}
              onChangeMax={handleRoomAreaMaxChange}
            />
          </InlineFilter>
        )}
        <InlineFilter prefix="Кухня">
          <RangeInput
            min={1}
            max={9999}
            valueMin={kitchenAreaMin}
            valueMax={kitchenAreaMax}
            onChangeMin={handleKitchenAreaMinChange}
            onChangeMax={handleKitchenAreaMaxChange}
          />
        </InlineFilter>
        {!isRoomOfferType && (
          <InlineFilter prefix="Жилая">
            <RangeInput
              min={1}
              max={9999}
              valueMin={livingAreaMin}
              valueMax={livingAreaMax}
              onChangeMin={handleLivingAreaMinChange}
              onChangeMax={handleLivingAreaMaxChange}
            />
          </InlineFilter>
        )}
      </InlineFilterGroup>
    </Filter>
  );
};
